/**
 * BasicInfoStep
 *
 * Story 11: Registration Wizard - Basic Information
 *
 * Step 2 of 5 in registration wizard
 *
 * Features:
 * - Company name input
 * - Contact name input
 * - Contact email input
 * - Contact phone input
 * - Password input with strength validation
 * - Confirm password input
 * - Form validation with Zod
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

export interface BasicInfoData {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  password: string;
  confirmPassword: string;
}

interface BasicInfoStepProps {
  onNext: (data: BasicInfoData) => void;
  onBack: () => void;
  initialData?: Partial<BasicInfoData>;
}

const basicInfoSchema = z.object({
  companyName: z.string().min(1, 'Company name is required'),
  contactName: z.string().min(1, 'Contact name is required'),
  contactEmail: z.string().email('Valid email is required'),
  contactPhone: z.string().min(10, 'Valid phone number is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type BasicInfoFormData = z.infer<typeof basicInfoSchema>;

export function BasicInfoStep({ onNext, onBack, initialData }: BasicInfoStepProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BasicInfoFormData>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: initialData,
  });

  const onSubmit = (data: BasicInfoFormData) => {
    onNext(data);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Basic Information</h2>
        <p className="text-neutral-600">
          Tell us about your company and create your account credentials.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="companyName" className="block text-sm font-medium text-neutral-700 mb-1">
            Company Name <span className="text-red-500">*</span>
          </label>
          <input
            id="companyName"
            type="text"
            {...register('companyName')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.companyName ? 'border-red-500' : ''}`}
            placeholder="ABC Hauling Services"
          />
          {errors.companyName && (
            <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="contactName" className="block text-sm font-medium text-neutral-700 mb-1">
            Contact Name <span className="text-red-500">*</span>
          </label>
          <input
            id="contactName"
            type="text"
            {...register('contactName')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.contactName ? 'border-red-500' : ''}`}
            placeholder="John Smith"
          />
          {errors.contactName && (
            <p className="mt-1 text-sm text-red-600">{errors.contactName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-neutral-700 mb-1">
            Contact Email <span className="text-red-500">*</span>
          </label>
          <input
            id="contactEmail"
            type="email"
            {...register('contactEmail')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.contactEmail ? 'border-red-500' : ''}`}
            placeholder="john@abchauling.com"
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.contactEmail.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="contactPhone" className="block text-sm font-medium text-neutral-700 mb-1">
            Contact Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="contactPhone"
            type="tel"
            {...register('contactPhone')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.contactPhone ? 'border-red-500' : ''}`}
            placeholder="(555) 123-4567"
          />
          {errors.contactPhone && (
            <p className="mt-1 text-sm text-red-600">{errors.contactPhone.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
            Password <span className="text-red-500">*</span>
          </label>
          <input
            id="password"
            type="password"
            {...register('password')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.password ? 'border-red-500' : ''}`}
            placeholder="Minimum 8 characters"
          />
          {errors.password && (
            <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-1">
            Confirm Password <span className="text-red-500">*</span>
          </label>
          <input
            id="confirmPassword"
            type="password"
            {...register('confirmPassword')}
            className={`input w-full px-4 py-3 md:py-2 text-base border rounded-lg ${errors.confirmPassword ? 'border-red-500' : ''}`}
            placeholder="Re-enter your password"
          />
          {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="btn-secondary sm:w-auto order-2 sm:order-1"
          >
            Back
          </button>
          <button
            type="submit"
            className="btn-primary sm:w-auto order-1 sm:order-2"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
}
